// Characters view - displays user's characters

import { css, html } from 'lit';
import { api, getCDNBaseUrl } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';
import { platformState } from '../../state/platform-state.js';
import '../shared/confirm-delete.js';

const userIconLarge = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="96"
  height="96"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="mb-3"
>
  <path d="M18 21a6 6 0 0 0-12 0" />
  <circle cx="12" cy="11" r="4" />
  <rect width="18" height="18" x="3" y="3" rx="2" />
</svg>`;

const userIconMedium = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="32"
  height="32"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="text-white"
>
  <path d="M18 21a6 6 0 0 0-12 0" />
  <circle cx="12" cy="11" r="4" />
  <rect width="18" height="18" x="3" y="3" rx="2" />
</svg>`;

// Plus icon SVG
const plusIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="18"
  height="18"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-1"
>
  <path d="M5 12h14" />
  <path d="M12 5v14" />
</svg>`;

/**
 * Characters view component
 * Displays the user's characters with options to view details or delete
 */
class CharactersView extends BaseComponent {
  static properties = {
    _characters: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _showDeleteModal: { type: Boolean, state: true },
    _characterToDelete: { type: Object, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .character-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1rem;
    }

    /* Mobile list view */
    .mobile-characters {
      padding: 1rem;
      background: #f8f9fa;
      min-height: 100%;
    }

    .mobile-new-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.35rem;
      width: 100%;
      padding: 0.75rem;
      margin-bottom: 0.75rem;
      font-size: 0.95rem;
      font-weight: 500;
      color: #0d6efd;
      background: #ffffff;
      border: 1px solid #dee2e6;
      border-radius: 10px;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
    }

    .mobile-new-btn:active {
      background: #e9ecef;
    }

    .mobile-list {
      list-style: none;
      margin: 0;
      padding: 0;
      background: #ffffff;
      border-radius: 10px;
      border: 1px solid #dee2e6;
      overflow: hidden;
    }

    .mobile-list-item {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      transition: background 0.1s ease;
      border-bottom: 1px solid #f0f0f0;
    }

    .mobile-list-item:last-child {
      border-bottom: none;
    }

    .mobile-list-item:active {
      background: #f0f0f0;
    }

    .mobile-token {
      width: 48px;
      height: 48px;
      border-radius: 8px;
      object-fit: cover;
      flex-shrink: 0;
    }

    .mobile-token-placeholder {
      width: 48px;
      height: 48px;
      border-radius: 8px;
      background: #6c757d;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .mobile-char-info {
      flex: 1;
      min-width: 0;
    }

    .mobile-char-name {
      font-size: 1rem;
      font-weight: 500;
      color: #212529;
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .mobile-char-detail {
      font-size: 0.8rem;
      color: #6c757d;
      margin: 0;
    }

    .mobile-chevron {
      color: #c0c0c0;
      font-size: 1.1rem;
      flex-shrink: 0;
    }

    .mobile-empty {
      text-align: center;
      padding: 3rem 1rem;
      color: #6c757d;
    }
  `;

  constructor() {
    super();
    /** @type {Array<import('../../types/jsdoc-types.js').Character & {tokenId?: string, hp?: number, maxHp?: number}>} */
    this._characters = [];
    this._loading = true;
    /** @type {string | null} */
    this._error = null;
    this._showDeleteModal = false;
    /** @type {import('../../types/jsdoc-types.js').Character | null} */
    this._characterToDelete = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadCharacters();
  }

  async _loadCharacters() {
    this._loading = true;
    this._error = null;

    try {
      this._characters =
        /** @type {Array<import('../../types/jsdoc-types.js').Character & {tokenId?: string, hp?: number, maxHp?: number}>} */ (
          await api.getCharacters()
        );
    } catch (err) {
      console.error('Failed to load characters:', err);
      this._error = 'Failed to load characters. Please try again.';
    } finally {
      this._loading = false;
    }
  }

  /** @param {{tokenId?: string}} character */
  _getCharacterTokenImage(character) {
    if (!character.tokenId || !getCDNBaseUrl()) return null;
    return `${getCDNBaseUrl()}/token/${character.tokenId}`;
  }

  /** @param {Event} e */
  _handleImageError(e) {
    /** @type {HTMLElement} */ (e.target).style.display = 'none';
  }

  /** @param {{id: string}} character */
  _viewCharacter(character) {
    router.navigate(`/characters/${character.id}`);
  }

  /** @param {import('../../types/jsdoc-types.js').Character} character */
  _handleDelete(character) {
    this._characterToDelete = character;
    this._showDeleteModal = true;
  }

  async _confirmDelete() {
    this._showDeleteModal = false;

    if (!this._characterToDelete) return;

    try {
      await api.deleteCharacter(this._characterToDelete.id);
      this._characterToDelete = null;
      await this._loadCharacters();
    } catch (err) {
      console.error('Failed to delete character:', err);
      this._error = 'Failed to delete character. Please try again.';
    }
  }

  _cancelDelete() {
    this._showDeleteModal = false;
    this._characterToDelete = null;
  }

  _handleNewCharacter() {
    router.navigate('/characters/new?skipRestore=true');
  }

  _renderMobile() {
    if (this._loading) {
      return html`
        <div class="mobile-characters">
          <div class="text-center text-muted py-5">
            <div class="spinner-border spinner-border-sm" role="status"></div>
            <span class="ms-2">Loading characters...</span>
          </div>
        </div>
      `;
    }

    if (this._error) {
      return html`<div class="mobile-characters"><div class="alert alert-danger">${this._error}</div></div>`;
    }

    if (this._characters.length === 0) {
      return html`
        <div class="mobile-characters">
          <div class="mobile-empty">
            ${userIconLarge}
            <p>No characters yet.</p>
            <button class="btn btn-primary" @click="${this._handleNewCharacter}">
              ${plusIcon} Create Character
            </button>
          </div>
        </div>
      `;
    }

    return html`
      <div class="mobile-characters">
        <button class="mobile-new-btn" @click="${this._handleNewCharacter}">
          ${plusIcon} New Character
        </button>
        <ul class="mobile-list">
          ${this._characters.map(
            (character) => html`
            <li class="mobile-list-item" @click="${() => this._viewCharacter(character)}">
              ${
                this._getCharacterTokenImage(character)
                  ? html`<img
                    class="mobile-token"
                    src="${this._getCharacterTokenImage(character)}"
                    alt=""
                    @error="${this._handleImageError}"
                  />`
                  : html`<div class="mobile-token-placeholder">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M18 21a6 6 0 0 0-12 0" /><circle cx="12" cy="11" r="4" /><rect width="18" height="18" x="3" y="3" rx="2" />
                    </svg>
                  </div>`
              }
              <div class="mobile-char-info">
                <p class="mobile-char-name">${character.name}</p>
                <p class="mobile-char-detail">Level ${character.level} ${character.class} · ${character.species}</p>
              </div>
              <i class="mobile-chevron bi bi-chevron-right"></i>
            </li>
          `,
          )}
        </ul>
      </div>

      ${
        this._showDeleteModal
          ? html`
            <confirm-delete
              ?show="${true}"
              heading="Delete Character"
              message="Are you sure you want to delete this character? This cannot be undone."
              @confirm="${this._confirmDelete}"
              @cancel="${this._cancelDelete}"
            ></confirm-delete>
          `
          : ''
      }
    `;
  }

  render() {
    if (platformState.isMobile) return this._renderMobile();
    return html`
      ${
        this._characters.length > 0
          ? html`
            <div class="mb-3 text-end">
              <button class="btn btn-primary" @click="${this._handleNewCharacter}">${plusIcon} New Character</button>
            </div>
          `
          : ''
      }

      <div>
        ${
          this._loading
            ? html`
              <div class="text-center text-muted py-5">
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-3">Loading characters...</p>
              </div>
            `
            : this._error
              ? html` <div class="alert alert-danger" role="alert">${this._error}</div> `
              : this._characters.length === 0
                ? html`
                  <div class="text-center text-muted py-5">
                    ${userIconLarge}
                    <p>No characters yet. Create your first character!</p>
                    <button class="btn btn-primary" @click="${this._handleNewCharacter}">
                      ${plusIcon} Create Character
                    </button>
                  </div>
                `
                : html`
                  <div class="character-grid">
                    ${this._characters.map(
                      (character) => html`
                        <div>
                          <div class="card h-100">
                            <div class="card-body d-flex gap-3">
                              ${
                                this._getCharacterTokenImage(character)
                                  ? html`
                                    <div class="flex-shrink-0">
                                      <img
                                        src="${this._getCharacterTokenImage(character)}"
                                        alt="Token"
                                        class="rounded"
                                        style="width: 64px; height: 64px; object-fit: cover;"
                                        @error="${this._handleImageError}"
                                      />
                                    </div>
                                  `
                                  : html`
                                    <div
                                      class="flex-shrink-0 bg-secondary rounded d-flex align-items-center justify-content-center"
                                      style="width: 64px; height: 64px;"
                                    >
                                      ${userIconMedium}
                                    </div>
                                  `
                              }
                              <div class="flex-grow-1">
                                <h5 class="card-title mb-1">${character.name}</h5>
                                <div class="text-muted small">
                                  <div>Level ${character.level} ${character.class}</div>
                                  <div>${character.species}</div>
                                  <div>HP: ${character.hp}/${character.maxHp}</div>
                                </div>
                              </div>
                            </div>
                            <div class="card-footer bg-white">
                              <div class="d-flex gap-2 justify-content-end">
                                <button
                                  class="btn btn-sm btn-outline-primary"
                                  @click="${() => this._viewCharacter(character)}"
                                >
                                  View Details
                                </button>
                                <button
                                  class="btn btn-sm btn-outline-danger"
                                  @click="${() => this._handleDelete(character)}"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      `,
                    )}
                  </div>
                `
        }
      </div>

      ${
        this._showDeleteModal
          ? html`
            <confirm-delete
              ?show="${true}"
              heading="Delete Character"
              message="Are you sure you want to delete this character? This cannot be undone."
              @confirm="${this._confirmDelete}"
              @cancel="${this._cancelDelete}"
            ></confirm-delete>
          `
          : ''
      }
    `;
  }
}

customElements.define('characters-view', CharactersView);
